import { useState, useEffect, useCallback, useRef } from 'react'
import Formsy from 'formsy-react'

import { Typography, Button, Tooltip, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
         DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox, FormControlLabel } from '@mui/material'
import { Add, Edit, Delete, Visibility } from '@mui/icons-material'

import _ from 'underscore'

import { D, Loading, SearchField, TRow, TCell, TextFieldFormsy, ConfirmDelete } from '../include'

// COMPONENTE INTERNO: Formulário de adiçao/edição
function AccessProfileForm({ permissionsData, row, edit, permission, dialogClose, api, alert, role }) {
  const [name, setName] = useState(row?.name ?? '')
  const [ids, setIds] = useState(row?.permission_id ?? [])
  const id = row?.id ?? null

  const handleTogglePermission = (permId) => {
    if (!permId) return
    setIds(prevIds => prevIds.includes(permId) ? _.without(prevIds, permId) : _.union(prevIds, [permId]))
  }

  const handleSave = () => {
    const payload = { name, ids }
    if (!edit) {
      api.post('/UserProfiles', payload).then(r => {
        dialogClose()
        alert(r.msg)
      })
    } else {
      api.put('/UserProfile/' + id, payload).then(r => {
        dialogClose()
        alert(r.msg)
      })
    }
  }

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '600px' }}>
        {!edit ? 'Novo perfil de acesso' : permission === 'w' ? 'Editar perfil de acesso' : 'Visualizar perfil de acesso'}
      </DialogTitle>

      <D style={{ paddingLeft: '24px', paddingRight: '24px' }}>
        <TextFieldFormsy
          value={name}
          onChange={v => setName(v)}
          type="text"
          name="name"
          label="Nome do perfil de acesso"
          disabled={permission !== 'w'}
        />
        <D h="1rem" />
        <Typography variant="h6" component="div">Permissões do perfil</Typography>
      </D>
        
      <DialogContent className='scD' style={{ maxHeight: '40vh' }}>
        {!!permissionsData && (
          <D className='scB' style={{ background: '#efefef', borderRadius: '5px' }}>
            {Object.keys(permissionsData).map(p => {
              
              // 🔒 se o usuário não for admin, não mostra a categoria "Permissão"
              // if (role !== 'admin' && ['Permissão', 'Painel de Controle'].includes(p)) return null

              const perm_read = _.findWhere(permissionsData[p], { name: "Ler" })
              const perm_write = _.findWhere(permissionsData[p], { name: "Escrever" })
              
              return (
                <D key={p} className="row aF" style={{ padding: '0.5rem' }}>
                  <D e>{p}</D>
                  <FormControlLabel
                    value="Ler" label="Ler" labelPlacement="end"
                    control={<Checkbox
                      checked={perm_read ? ids.includes(perm_read.id) : false}
                      onChange={() => handleTogglePermission(perm_read?.id)}
                      disabled={permission !== 'w'} />}
                  />
                  <FormControlLabel
                    value="Escrever" label="Escrever" labelPlacement="end"
                    control={<Checkbox
                      checked={perm_write ? ids.includes(perm_write.id) : false}
                      onChange={() => handleTogglePermission(perm_write?.id)}
                      disabled={permission !== 'w'} />}
                  />
                </D>
              )
            })}
          </D>
        )}
      </DialogContent>

      <D h="1rem" />
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>Cancelar</Button>
        {permission === 'w' && (
          <Button variant="outlined" color="primary" onClick={handleSave}>Salvar</Button>
        )}
      </DialogActions>
    </Formsy >
  )
}


// COMPONENTE PRINCIPAL
export function AccessProfile({ api, user, permission, alert, dialog, dialogClose }) {
  const [searchField, setSearchField] = useState('')
  const [dataTable, setDataTable] = useState([])
  const [permissionsData, setPermissionsData] = useState([])
  const [loading, setLoading] = useState(true)

  // Ref para proteger a dupla renderização do Strict Mode
  const isFirstRender = useRef(true)

  // Memoiza a função para mantê-la estável na memória
  const updateData = useCallback(() => {
    setLoading(true)
    
    api.get('/UserProfiles').then(r => {
      setDataTable(r)
      setLoading(false)
    })
    
    api.get('/PermissionsList').then(r => {
      setPermissionsData(_.groupBy(r, 'category'))
    })
  }, [api])

  // Efeito blindado pelo portão do useRef
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false // Tranca a porta instantaneamente
      updateData()
    }
  }, [updateData])

  const handleOpenDialogForm = (row = null, isEdit = false) => {
    dialog(
      <AccessProfileForm 
        edit={isEdit}
        row={row}
        permissionsData={permissionsData}
        api={api}
        alert={alert}
        permission={permission}
        role={user.role}
        dialogClose={() => {
          updateData()
          dialogClose()
        }}
      />
    )
  }

  const handleOpenDelete = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir o perfil de acesso:"
        itemName={row.name}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/UserProfile/${row.id}`)
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir o perfil de acesso.'));
        }}
      />
    )
  }

  // Lógica de filtro em tempo de renderização (Não guarda HTML no state!)
  let filteredRows = dataTable
  
  if (user.role !== 'admin') {
    filteredRows = filteredRows.filter(r => r.name !== 'admin')
  }

  if (searchField) {
    const searchLower = searchField.toLowerCase()
    filteredRows = filteredRows.filter(r => 
      Object.values(r).some(val => String(val).toLowerCase().includes(searchLower))
    )
  }

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      <D className="row w aF">
        <SearchField value={searchField} onChange={v => setSearchField(v)} />
        <D e /><D size="2rem" />
        {permission === 'w' &&
          <Button variant="contained" color="primary" onClick={() => handleOpenDialogForm()}>
            <Add />Perfil de Acesso
          </Button>
        }
      </D>
      <D h="1rem" />

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow>
                <TCell align="center">{permission === 'w' ? 'Editar' : 'Visualizar'}</TCell>
                {permission === 'w' && <TCell align="center">Excluir</TCell>}
                <TCell>ID</TCell>
                <TCell width="100%">Nome do Perfil</TCell>
              </TRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TRow><TCell component="th" align="center" colSpan={6}><Loading /></TCell></TRow>
              ) : filteredRows.length > 0 ? (
                filteredRows.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TCell align="center" component="th">
                      <Tooltip title={permission === 'w' ? 'Editar' : 'Visualizar'} arrow>
                        <IconButton style={{ color: '#aaa' }} onClick={() => handleOpenDialogForm(row, true)}>
                          {permission === 'w' ? <Edit /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </TCell>
                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton style={{ color: '#aaa' }} onClick={() => handleOpenDelete(row)}>
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    <TCell component="th">{row.id}</TCell>
                    <TCell component="th">{row.name}</TCell>
                  </TRow>
                ))
              ) : (
                <TRow><TCell component="th" align="center" colSpan={6}>Nenhum perfil encontrado</TCell></TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  )
}